import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, RefreshCw, Trash2, XCircle, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, fmtDate } from '../../../lib/adminApi';

const STATUS_COLOR = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};

export const InvitesHubTab = () => {
  const [items, setItems] = useState([]);
  const [expired, setExpired] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.managerRequests();
      setItems(r?.requests || r || []);
      setExpired(r?.expired_count || 0);
    } catch (err) {
      toast.error(err.message || 'Failed to load invites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete this manager request?')) return;
    setDeleting(id);
    try {
      await adminApi.deleteManagerRequest(id);
      toast.success('Request deleted');
      load();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeleting('');
    }
  };

  const buckets = {
    pending: items.filter((r) => r.status === 'pending'),
    approved: items.filter((r) => r.status === 'approved'),
    rejected: items.filter((r) => r.status === 'rejected'),
  };

  return (
    <div data-testid="tab-invites" className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 flex items-center gap-3">
          <Clock className="w-8 h-8 text-amber-600" />
          <div><div className="text-2xl font-bold text-amber-700">{buckets.pending.length}</div><div className="text-xs text-amber-700">Pending</div></div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 flex items-center gap-3">
          <CheckCircle className="w-8 h-8 text-emerald-600" />
          <div><div className="text-2xl font-bold text-emerald-700">{buckets.approved.length}</div><div className="text-xs text-emerald-700">Approved</div></div>
        </div>
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4 flex items-center gap-3">
          <XCircle className="w-8 h-8 text-rose-600" />
          <div><div className="text-2xl font-bold text-rose-700">{buckets.rejected.length}</div><div className="text-xs text-rose-700">Rejected</div></div>
        </div>
      </div>

      {expired > 0 && (
        <div className="text-xs px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
          {expired} stale invitation(s) auto-expired and were removed. Resend if needed.
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
          <UserPlus className="w-4 h-4" /> Manager Invitations ({items.length})
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-slate-300 hover:bg-slate-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading && <div className="text-sm text-slate-500 p-6">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-sm text-slate-500 p-6 text-center">No manager invitations.</div>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide">Employee</th>
                <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide">Manager</th>
                <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide">Sent</th>
                <th className="px-4 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b last:border-b-0 border-slate-100">
                  <td className="px-4 py-2.5 text-slate-700">{r.requester_email || r.requester_id}</td>
                  <td className="px-4 py-2.5 text-slate-700">{r.manager_email}</td>
                  <td className="px-4 py-2.5"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${STATUS_COLOR[r.status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>{r.status}</span></td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => handleDelete(r.id)}
                      disabled={deleting === r.id}
                      data-testid={`invite-delete-${r.id}`}
                      className="text-slate-400 hover:text-rose-600 p-1 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
